import React, { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  Building,
  User,
  IndianRupee,
  Calendar,
  Plus,
  CheckCircle2,
  Clock,
  AlertTriangle,
  Edit,
  Trash2,
  ExternalLink,
  CreditCard,
  Package
} from 'lucide-react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { DeliverableStatusBadge } from '@/components/deliverables/DeliverableStatusBadge';
import { DeliverableCategoryBadge } from '@/components/deliverables/DeliverableCategoryBadge';
import { AddDeliverableModal } from '@/components/deliverables/AddDeliverableModal';
import { Deliverable } from '@/types/deliverables';
import { Lead } from '@/types/lead';
import { cn } from '@/lib/utils';
import { toDeliverablesArray } from '@/lib/deliverablesResponse';
import { useToast } from '@/hooks/use-toast';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

export const ClientDeliverablesPage: React.FC = () => {
  const { leadId } = useParams<{ leadId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [showAddModal, setShowAddModal] = useState(false);
  const [editingDeliverable, setEditingDeliverable] = useState<Deliverable | null>(null);

  // Fetch lead details
  const { data: lead, isLoading: leadLoading } = useQuery<Lead>({
    queryKey: ['lead', leadId],
    queryFn: async () => {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/leads/${leadId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error('Failed to fetch lead');
      const result = await response.json();
      return result.data || result;
    },
    enabled: !!leadId
  });

  // Fetch deliverables for this lead
  const { data: deliverables = [], isLoading: deliverablesLoading } = useQuery<Deliverable[]>({
    queryKey: ['deliverables', 'by-lead', leadId],
    queryFn: async () => {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/deliverables/by-lead/${leadId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) {
        // Fallback to filtered deliverables endpoint
        if (response.status === 404) {
          const fallbackResponse = await fetch(`${API_BASE_URL}/deliverables?leadId=${leadId}`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          if (!fallbackResponse.ok) throw new Error('Failed to fetch deliverables');
          const result = await fallbackResponse.json();
          return toDeliverablesArray(result);
        }
        throw new Error('Failed to fetch deliverables');
      }
      const result = await response.json();
      return toDeliverablesArray(result);
    },
    enabled: !!leadId
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/deliverables/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (!response.ok) throw new Error('Failed to delete deliverable');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deliverables', 'by-lead', leadId] });
      toast({
        title: 'Success',
        description: 'Deliverable deleted successfully'
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive'
      });
    }
  });

  // Mark as delivered mutation
  const markDeliveredMutation = useMutation({
    mutationFn: async (id: string) => {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/deliverables/${id}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          status: 'DELIVERED',
          deliveredDate: new Date().toISOString()
        })
      });
      if (!response.ok) throw new Error('Failed to update deliverable');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deliverables', 'by-lead', leadId] });
      toast({
        title: 'Success',
        description: 'Marked as delivered'
      });
    }
  });

  // Mark as verified mutation
  const markVerifiedMutation = useMutation({
    mutationFn: async (id: string) => {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/deliverables/${id}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: 'VERIFIED' })
      });
      if (!response.ok) throw new Error('Failed to update deliverable');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deliverables', 'by-lead', leadId] });
      toast({
        title: 'Success',
        description: 'Marked as verified'
      });
    }
  });

  // Calculate progress stats
  const progressStats = useMemo(() => {
    const total = deliverables.length;
    if (total === 0) return { completed: 0, percentage: 0, overdue: 0 };

    const completed = deliverables.filter(d =>
      d.status === 'DELIVERED' || d.status === 'VERIFIED'
    ).length;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const overdue = deliverables.filter(d => {
      if (d.status === 'DELIVERED' || d.status === 'VERIFIED') return false;
      return new Date(d.committedDate) < today;
    }).length;

    return {
      completed,
      percentage: Math.round((completed / total) * 100),
      overdue,
      total
    };
  }, [deliverables]);

  // Get payment status badge color
  const getPaymentStatusColor = (stage: string) => {
    if (stage === 'WON') return 'bg-green-100 text-green-700';
    return 'bg-gray-100 text-gray-700';
  };

  // Calculate days status for a deliverable
  const getDaysStatus = (deliverable: Deliverable) => {
    if (deliverable.status === 'DELIVERED' || deliverable.status === 'VERIFIED') {
      return { label: 'Completed', className: 'text-green-600' };
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const committedDate = new Date(deliverable.committedDate);
    committedDate.setHours(0, 0, 0, 0);

    const diffTime = committedDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) {
      return {
        label: `${Math.abs(diffDays)} days overdue`,
        className: 'text-red-600 font-semibold'
      };
    } else if (diffDays === 0) {
      return { label: 'Due today', className: 'text-orange-600 font-semibold' };
    } else if (diffDays === 1) {
      return { label: 'Due tomorrow', className: 'text-yellow-600' };
    } else {
      return { label: `${diffDays} days left`, className: 'text-gray-600' };
    }
  };

  const handleDelete = (id: string) => {
    if (window.confirm('Are you sure you want to delete this deliverable?')) {
      deleteMutation.mutate(id);
    }
  };

  const handleEdit = (deliverable: Deliverable) => {
    setEditingDeliverable(deliverable);
    setShowAddModal(true);
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
    setEditingDeliverable(null);
  };

  const isLoading = leadLoading || deliverablesLoading;

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
        </div>
      </DashboardLayout>
    );
  }

  if (!lead) {
    return (
      <DashboardLayout>
        <div className="flex flex-col items-center justify-center h-96">
          <AlertTriangle className="w-12 h-12 text-red-500 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Lead not found</h3>
          <button
            onClick={() => navigate('/deliverables')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Back to Deliverables
          </button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Back Button */}
        <button
          onClick={() => navigate('/deliverables/kanban')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Kanban
        </button>

        {/* Client Header */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
            {/* Client Info */}
            <div className="flex items-start gap-4">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
                <Building className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{lead.name}</h1>
                <p className="text-gray-600">{lead.company}</p>
                <div className="flex items-center gap-4 mt-2">
                  {lead.contactPerson && (
                    <div className="flex items-center gap-1 text-sm text-gray-500">
                      <User className="w-4 h-4" />
                      {lead.contactPerson}
                    </div>
                  )}
                  {lead.email && (
                    <a
                      href={`mailto:${lead.email}`}
                      className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700"
                    >
                      <ExternalLink className="w-3 h-3" />
                      {lead.email}
                    </a>
                  )}
                </div>
              </div>
            </div>

            {/* Stats */}
            <div className="flex flex-wrap gap-4">
              {/* Deal Value */}
              <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3">
                <div className="flex items-center gap-2">
                  <IndianRupee className="w-4 h-4 text-green-600" />
                  <span className="text-sm text-green-700">Deal Value</span>
                </div>
                <p className="text-xl font-bold text-green-800 mt-1">
                  ₹{(lead.dealValue || 0).toLocaleString('en-IN')}
                </p>
              </div>

              {/* Payment Status */}
              <div className={cn(
                'border rounded-lg px-4 py-3',
                getPaymentStatusColor(lead.stage)
              )}>
                <div className="flex items-center gap-2">
                  <CreditCard className="w-4 h-4" />
                  <span className="text-sm">Status</span>
                </div>
                <p className="text-xl font-bold mt-1">{lead.stage}</p>
              </div>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Package className="w-4 h-4 text-gray-500" />
                <span className="text-sm font-medium text-gray-700">
                  Deliverables Progress
                </span>
              </div>
              <span className="text-sm text-gray-600">
                {progressStats.completed} of {progressStats.total} completed ({progressStats.percentage}%)
              </span>
            </div>
            <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-gradient-to-r from-blue-500 to-green-500 rounded-full"
                initial={{ width: 0 }}
                animate={{ width: `${progressStats.percentage}%` }}
                transition={{ duration: 0.5, ease: 'easeOut' }}
              />
            </div>
            {progressStats.overdue > 0 && (
              <div className="flex items-center gap-1 mt-2 text-sm text-red-600">
                <AlertTriangle className="w-4 h-4" />
                {progressStats.overdue} overdue deliverable{progressStats.overdue !== 1 ? 's' : ''}
              </div>
            )}
          </div>
        </motion.div>

        {/* Actions Bar */}
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">
            Deliverables ({deliverables.length})
          </h2>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Deliverable
          </button>
        </div>

        {/* Deliverables List */}
        <div className="space-y-4">
          {deliverables.length === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="bg-white rounded-lg border border-gray-200 p-12 text-center"
            >
              <Package className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No deliverables yet
              </h3>
              <p className="text-gray-500 mb-4">
                Add deliverables to track commitments for this client
              </p>
              <button
                onClick={() => setShowAddModal(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Add First Deliverable
              </button>
            </motion.div>
          ) : (
            deliverables.map((deliverable, index) => {
              const daysStatus = getDaysStatus(deliverable);
              return (
                <motion.div
                  key={deliverable.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white rounded-lg border border-gray-200 p-5 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <DeliverableStatusBadge status={deliverable.status} />
                        <DeliverableCategoryBadge category={deliverable.category} />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">
                        {deliverable.title}
                      </h3>
                      {deliverable.description && (
                        <p className="text-gray-600 text-sm mb-3">
                          {deliverable.description}
                        </p>
                      )}
                      <div className="flex items-center gap-6 text-sm">
                        <div className="flex items-center gap-1 text-gray-500">
                          <Calendar className="w-4 h-4" />
                          Due: {new Date(deliverable.committedDate).toLocaleDateString()}
                        </div>
                        <span className={daysStatus.className}>
                          {daysStatus.label}
                        </span>
                        {deliverable.assignedToUser && (
                          <div className="flex items-center gap-1 text-gray-500">
                            <User className="w-4 h-4" />
                            {deliverable.assignedToUser.name}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 ml-4">
                      {deliverable.status !== 'DELIVERED' && deliverable.status !== 'VERIFIED' && (
                        <button
                          onClick={() => markDeliveredMutation.mutate(deliverable.id)}
                          disabled={markDeliveredMutation.isPending}
                          className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-green-700 bg-green-100 rounded-lg hover:bg-green-200 transition-colors"
                        >
                          <CheckCircle2 className="w-4 h-4" />
                          Deliver
                        </button>
                      )}
                      {deliverable.status === 'DELIVERED' && (
                        <button
                          onClick={() => markVerifiedMutation.mutate(deliverable.id)}
                          disabled={markVerifiedMutation.isPending}
                          className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-emerald-700 bg-emerald-100 rounded-lg hover:bg-emerald-200 transition-colors"
                        >
                          <CheckCircle2 className="w-4 h-4" />
                          Verify
                        </button>
                      )}
                      <button
                        onClick={() => handleEdit(deliverable)}
                        className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(deliverable.id)}
                        className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </motion.div>
              );
            })
          )}
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showAddModal && (
        <AddDeliverableModal
          isOpen={showAddModal}
          onClose={handleCloseModal}
          deliverable={editingDeliverable}
          preselectedLeadId={leadId}
        />
      )}
    </DashboardLayout>
  );
};
